import { ACCESS_TOKEN_KEY } from "../base/private/axiosPrivateClient";

/** Типы событий SSE-пайплайна генерации AI-отчётов. */
export type PipelineReportType =
  | "transcribe"
  | "actions"
  | "q_density"
  | "answers"
  | "environment"
  | "evaluation";

export interface PipelineStatusEvent {
  stage: string;
  total_steps: number;
  report_types: PipelineReportType[];
}

export interface PipelineProgressEvent {
  percent?: number;
  stage?: string;
  message?: string;
  /** Внутренние sub-steps AI-генератора (только это поле, без percent/stage). */
  step?: string;
}

export interface PipelineResultEvent {
  report_type: PipelineReportType;
  data: any;
}

export interface PipelineWarningEvent {
  case?: string;
  summary_ru?: string;
  summary_en?: string;
  summary_kz?: string;
}

export interface PipelineErrorEvent {
  detail?: string;
}

export interface PipelineHandlers {
  status?: (data: PipelineStatusEvent) => void;
  progress?: (data: PipelineProgressEvent) => void;
  result?: (data: PipelineResultEvent) => void;
  done?: (data: { ok: boolean }) => void;
  warning?: (data: PipelineWarningEvent) => void;
  error?: (data: PipelineErrorEvent) => void;
}

/** URL SSE-эндпоинта пайплайна на rating-сервисе. */
export function buildPipelineUrl(attemptId: number, forceRegenerate = false): string {
  const base = process.env.NEXT_PUBLIC_API_RATING_URL ?? "";
  const qs = forceRegenerate ? "?force_regenerate=true" : "";
  return `${base}reports/ai/pipeline/${attemptId}/stream${qs}`;
}

/**
 * Читает SSE-поток (text/event-stream) из fetch Response и раздаёт события в handlers.
 * Используется fetch (а не EventSource), т.к. нужен Bearer-токен в заголовке.
 */
export async function readSSE(
  res: Response,
  handlers: PipelineHandlers,
  signal?: AbortSignal,
): Promise<void> {
  if (!res.body) throw new Error("SSE: empty response body");

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";

  try {
    while (true) {
      if (signal?.aborted) break;
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });

      const blocks = buf.split("\n\n");
      buf = blocks.pop() ?? ""; // незавершённый блок оставляем в буфере

      for (const block of blocks) {
        const lines = block.trim().split("\n");
        const eventLine = lines.find((l) => l.startsWith("event:"));
        const dataLine = lines.find((l) => l.startsWith("data:"));
        if (!eventLine || !dataLine) continue;

        const event = eventLine.replace("event:", "").trim();
        let data: any = null;
        try {
          data = JSON.parse(dataLine.replace("data:", "").trim());
        } catch {
          continue; // битый JSON — пропускаем
        }

        (handlers as Record<string, ((d: any) => void) | undefined>)[event]?.(data);
      }
    }
  } finally {
    try {
      await reader.cancel();
    } catch {
      /* noop */
    }
  }
}

/** Открыть SSE-поток пайплайна. Бросает при не-2xx. */
export async function openPipelineStream(
  attemptId: number,
  forceRegenerate: boolean,
  signal: AbortSignal,
): Promise<Response> {
  const token =
    typeof window !== "undefined" ? localStorage.getItem(ACCESS_TOKEN_KEY) : null;

  const res = await fetch(buildPipelineUrl(attemptId, forceRegenerate), {
    method: "GET",
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      Accept: "text/event-stream",
    },
    signal,
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res;
}
